'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { VariantProps } from 'tailwind-variants';

import IconBrand from '@/fo/icons/base/icons/bonus_icon.svg';
import IconDelete from '@/fo/icons/base/icons/delete.svg';
import IconInfo from '@/fo/icons/base/icons/info.svg';
import { UiKitButtonBase } from '@/fo/ui/kit/button/base';
import { UiKitLabelBase } from '@/fo/ui/kit/label/base';
import { UiKitPriceBase } from '@/fo/ui/kit/price/base';
import { UiKitTooltipBase } from '@/fo/ui/kit/tooltip/base';
import { useMediaQuery } from '@/fo/ui/mod/media/query';
import { UiProductCounterInputBase } from '@/fo/ui/product/counter-input/base';
import { UiProductOptionsBase } from '@/fo/ui/product/options/base';
import { UiProductWishlistButton } from '@/fo/ui/product/wishlist/button';
import { utilEventCartModal } from '@/fo/util/event-cart-modal';
import {
  MappedProduct,
  Option,
  CartItemConfigurableOption
} from '@/fo/util/map-product-data';
import { utilPluralize } from '@/fo/util/pluralize';

import { GroupItems } from '../components/GroupItems/GroupItems';
import { ProductInGroupTile } from '../components/ProductInGroupTile/ProductInGroupTile';

import { styles } from './UiProductTileSecondary.styles';

type Variants = VariantProps<typeof styles>;
type NativeElement = React.ComponentPropsWithoutRef<'div'>;

export interface UiProductTileSecondaryProps
  extends Omit<NativeElement, 'onClick'> {
  type?: Variants['type'];
  className?: string;
  classNameActions?: string;
  /** Авторизован ли пользователь */
  isAuthorized?: boolean;
  /** Ссылка на страницу товара */
  link?: string;
  /** Изображение товара */
  image: string;
  /** Название бренда */
  brand?: {
    color: string;
    name: string;
    url: string;
  };
  /** Название товара */
  title: string;
  /** Описание товара */
  description: string;
  /** Текущая цена */
  price: number;
  /** Старая цена */
  oldPrice?: number | null;
  /** Скидка */
  salePrice?: number;
  /** Валюта */
  currency?: string;
  /** Количество баллов лояльности */
  bonuses?: number;
  bonusesMax?: number;
  /** Опции товара (объем, цвет и т.д.) */
  options?: Option[];
  /** Обработчик клика по карточке */
  onClick?: NativeElement['onClick'];
  /** Обработчик добавления в корзину */
  onCartClick?: () => void;
  /** Обработчик изменения количества */
  onCartChange?: (count: number) => void;
  /** Обработчик клика по опции */
  onOptionClick?: (option: Option) => void;
  /** Выбранные атрибуты для товаров в корзине */
  selectedAttributes?: CartItemConfigurableOption[];
  /** Обработчик клика по избранному */
  onWishlistClick?: () => void;
  /** Обработчик клика по удалению */
  onRemoveClick?: () => void;
  /** Текущее количество */
  count?: number;
  /** В избранном */
  isInWishlist?: boolean;
  /** Тип группы */
  groupType?: number | null;
  /** Дочерние товары */
  childrenItems?: MappedProduct[];
  /** Обработчик клика по избранному для дочерних товаров */
  onWishlistChildrenClick?: (
    sku: string,
    wishlistSku: string,
    parentSku?: string,
    wishlistItemId?: string,
    isInWishlist?: boolean
  ) => void;
  /** Ссылка на дочерний товар */
  childrenLink?: (sku: string) => string;
  /** Корзина */
  isCart?: boolean;
  /** Загрузка кнопки корзины */
  isCartLoading?: boolean;
  /** Mindbox ID */
  mindboxId?: string;
  /** Максимальное количество */
  maxCount?: number;
  mindboxBonuses?: number;
}

export function UiProductTileSecondary({
  type = 'base',
  className,
  classNameActions,
  link,
  image,
  title,
  price,
  oldPrice,
  salePrice = 0,
  currency,
  options,
  onClick,
  onCartClick,
  onCartChange,
  onOptionClick,
  selectedAttributes,
  onWishlistClick,
  onRemoveClick,
  count = 1,
  isInWishlist,
  groupType,
  childrenItems,
  onWishlistChildrenClick,
  isCart,
  isCartLoading,
  maxCount,
  mindboxBonuses,
  isAuthorized
}: UiProductTileSecondaryProps) {
  const t = useTranslations();
  const isMobile = useMediaQuery('md', 'max');
  const slots = styles({ type });
  const percent = oldPrice ? 100 - Math.round((price * 100) / oldPrice) : 0;
  const percentBlock = percent > 0 && (
    <UiKitLabelBase
      className={slots.percentLabel()}
      color="brand"
    >
      -{percent}%
    </UiKitLabelBase>
  );
  const pluralizeBalance = utilPluralize({
    one: t('pluralize.balance.one'),
    few: t('pluralize.balance.few'),
    many: t('pluralize.balance.many')
  });
  return (
    <div className={slots.tileWrapper()}>
      {percentBlock}
      <div
        className={slots.base({ className })}
        onClick={onClick}
      >
        {link && (
          <Link
            className={slots.link()}
            href={link}
            onClick={() => utilEventCartModal.close()}
          />
        )}

        <div className={slots.imageContainer()}>
          <Link
            href={link ?? ''}
            onClick={() => utilEventCartModal.close()}
          >
            <Image
              className={slots.image()}
              src={image}
              alt={title}
              width={120}
              height={120}
            />
          </Link>
          {isCart && count > 0 && isMobile && (
            <button
              type="button"
              className={slots.actionItem()}
              onClick={onRemoveClick}
            >
              <IconDelete className="size-6" />
            </button>
          )}
        </div>

        <div className={slots.content()}>
          <div className={slots.info()}>
            <h2 className={slots.title()}>
              <span className={slots.titleText()}>{title}</span>
              <div className={slots.productActions()}>
                <div className={slots.actions({ className: classNameActions })}>
                  {price > 0 && (
                    <UiProductWishlistButton
                      className={slots.wishlistItem()}
                      isActive={Boolean(isInWishlist)}
                      onClick={onWishlistClick}
                    />
                  )}
                  {isCart && count > 0 && !isMobile && (
                    <button
                      type="button"
                      className={slots.actionItem()}
                      onClick={onRemoveClick}
                    >
                      <IconDelete className="size-6" />
                    </button>
                  )}
                </div>

                {type === 'base' && count === 0 && onCartClick && (
                  <UiKitButtonBase
                    className={slots.buy()}
                    color="primary"
                    onClick={onCartClick}
                    loading={isCartLoading}
                  >
                    {t('To cart')}
                  </UiKitButtonBase>
                )}

                <div className="my-auto flex flex-col gap-4">
                  {!isCart && type !== 'base' && count > 0 && (
                    <div className={slots.count()}>
                      {count} {t('unit-pieces')}
                    </div>
                  )}
                </div>
              </div>
            </h2>

            {type === 'unavailable' && (
              <div className={slots.outOfStockText()}>{t('Out of stock')}</div>
            )}

            {/* Отображение выбранных атрибутов для товаров в корзине */}
            {isCart && selectedAttributes && selectedAttributes.length > 0 && (
              <div className="mt-2 text-xs text-gray-600">
                {selectedAttributes.map((attr) => (
                  <div key={attr.id}>
                    {attr.option_label}: {attr.value_label}
                  </div>
                ))}
              </div>
            )}

            {!!mindboxBonuses && mindboxBonuses > 0 && !isMobile && (
              <p className={slots.points()}>
                <IconBrand className={slots.pointsIcon()} />
                {mindboxBonuses} {pluralizeBalance(mindboxBonuses)}
                <UiKitTooltipBase
                  content={
                    <div className={slots.tooltip()}>
                      {isAuthorized
                        ? t('Note for bonuses')
                        : t('Note for bonuses for guest')}
                    </div>
                  }
                >
                  <IconInfo className={slots.pointsInfoIcon()} />
                </UiKitTooltipBase>
              </p>
            )}

            {(type === 'base' || type === 'gift' || type === 'order') && (
              <div className={slots.priceCounterWrapper()}>
                <div className={slots.priceRow()}>
                  {salePrice > 0 && (
                    <div className={slots.salePrice()}>
                      <span>{t('discount-title')}</span>
                      <UiKitPriceBase
                        value={salePrice}
                        currency={currency}
                      />
                    </div>
                  )}
                  <UiKitPriceBase
                    value={price}
                    oldValue={oldPrice}
                    className={slots.price()}
                    classNameValue="heading-xs font-[450] max-md:heading-m-intr-regular"
                    classNameOldValue="heading-xs md:mt-auto text-gray-500 max-md:text-[12px]"
                    currency={currency}
                  />
                </div>

                {!!mindboxBonuses && mindboxBonuses > 0 && isMobile && (
                  <p className={slots.points()}>
                    <IconBrand className={slots.pointsIcon()} />
                    {mindboxBonuses} {pluralizeBalance(mindboxBonuses)}
                    <UiKitTooltipBase
                      content={
                        <div className={slots.tooltip()}>
                          {isAuthorized
                            ? t('Note for bonuses')
                            : t('Note for bonuses for guest')}
                        </div>
                      }
                    >
                      <IconInfo className={slots.pointsInfoIcon()} />
                    </UiKitTooltipBase>
                  </p>
                )}

                {type === 'base' && (count !== 0 || !onCartClick) && (
                  <>
                    {price > 0 ? (
                      <UiProductCounterInputBase
                        className={slots.controls()}
                        value={count}
                        onDebounceChange={onCartChange}
                        loading={isCartLoading}
                        max={maxCount}
                      />
                    ) : (
                      <div className={slots.countFree()}>
                        {count} {t('unit-pieces')}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {options && options.length > 0 && !isCart && (
            <UiProductOptionsBase
              options={options as Option[]}
              size="s"
              onOptionClick={onOptionClick}
              maxVisible={3}
              noSelected={true}
            />
          )}
        </div>
      </div>

      <GroupItems
        data={childrenItems}
        groupType={groupType}
      >
        {childrenItems?.map((item) => (
          <ProductInGroupTile
            key={item.sku}
            type={type}
            data={item}
            onWishlistClick={onWishlistChildrenClick}
            currency={currency}
          />
        ))}
      </GroupItems>
    </div>
  );
}
